/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { observer } from 'mobx-react';
import { Box, Grid, makeStyles, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { VerticalBarLoader, ProgressBarComponent, SelectField, Paper, PaperBorderType, Button, ConfirmDialog } from '@roc/ui';
import { useLoanStore } from '@roc/feature-loans';
import { useExtensions } from './hooks/useExtensions';
import { VirtualScroll } from './components/common/virtualScroll';
import ExtensionsLoanActors from './components/loan-actors/extensionsLoanActors';
import { ExtensionTodos } from 'libs/feature-loans/src/loans/loanDetails/extensions/components/extensionTodos';
import { RequestExtensionDialog } from 'libs/feature-loans/src/loans/loanDetails/extensions/components/requestExtensionDialog';
import { isNil } from '@roc/feature-utils';
import { useLoanFlags } from './hooks/useLoanFlags';
import {
  EXTENSION_STATUS
} from './utils/extensionsConstants';

const useStyles = makeStyles(() => ({
  gridContainer: {
    height: 'fit-content',
    padding: '10px',
  },
  gridHeader: {
    padding: 16,
  },
}));

export const ExtensionsInternal = observer(() => {
  const classes = useStyles();
  const extensionsHook = useExtensions();
  const { extensionsStore } = extensionsHook;
  const { loanStore } = useLoanStore();
  const flags = useLoanFlags();

  return (
    <>
      <VirtualScroll>
        <Grid container className={classes.gridContainer} spacing={2}>
          {flags.loanDqFlagLabel && (
            <Grid item xs={12}>
              <Alert severity="error">{flags.loanDqFlagLabel}</Alert>
            </Grid>
          )}
          {extensionsHook.extensionsStore.isLoading && <VerticalBarLoader />}
          {!extensionsHook.extensionsStore.isLoading && extensionsHook.hasLoanExtensions && (<>
            <Grid item xs={11}>
              <ProgressBarComponent
                allowNonExistingStep
                progressArray={extensionsHook.progressSteps}
                active={extensionsHook.activeStep}
              />
            </Grid>
            <Grid item xs={1}>
              <Grid container direction='row' justifyContent='flex-end'>
                <Grid item>
                  <SelectField
                    fullWidth
                    standaloneLabel
                    variant="outlined"
                    label='Extension #'
                    style={{ minWidth: 140 }}
                    testId={'selectedExtension'}
                    options={extensionsHook.extensionOptions}
                    value={extensionsHook.selectedExtensionId}
                    onChange={value => extensionsHook.handleSelectExtension(value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
          )}
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              spacing={2}
            >
              <Grid item xs={6}>
                <ExtensionsLoanActors loan={loanStore.loanDetails} specialistsDropdown={extensionsHook.specialistsDropdown} />
              </Grid>
              <Grid item xs={6}>
                <Paper borderType={PaperBorderType.SOLID_BORDER} titleComponent={
                  <Grid container direction='row' justifyContent='space-between' className={classes.gridHeader}>
                    <Grid item>
                      <Typography variant='h5' component='h5'>Actions</Typography>
                    </Grid>
                  </Grid>
                }>
                  <Grid container direction='row' spacing={2} style={{ padding: 16 }}>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="requestExtension"
                        onClick={extensionsHook.handleRequestExtension}
                        disabled={flags.blockExtensionRequest || !!(
                          extensionsStore.selectedExtension &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.COMPLETED &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.RETRACTED
                        )}
                      >
                        Request Extension
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="requestExtension"
                        onClick={() => extensionsHook.setIsMarkInsuranceReviewCompleteDialogOpen(true)}
                        disabled={!(
                          extensionsStore.selectedExtension?.status === EXTENSION_STATUS.INSURANCE_UNDER_REVIEW && flags.extensionActionsEnabled
                        )}
                      >
                        Mark Insurance Complete
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="requestExtension"
                        onClick={() => extensionsHook.setIsRequestExtensionAgreementDialogOpen(true)}
                        disabled={!(
                          extensionsStore.selectedExtension?.status === EXTENSION_STATUS.INSURANCE_REVIEW_COMPLETED && flags.extensionActionsEnabled
                        )}
                      >
                        Request Extension Agreement
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="requestExtension"
                        onClick={() => extensionsHook.setIsMarkAsCompleteDialogOpen(true)}
                        disabled={!(
                          flags.extensionActionsEnabled &&
                          extensionsStore.selectedExtension?.status &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.COMPLETED &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.RETRACTED
                        )}
                      >
                        Mark Extension as Completed
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="markExtensionAsRetracted"
                        onClick={() => extensionsHook.setIsMarkAsRetractedDialogOpen(true)}
                        disabled={!(
                          flags.extensionActionsEnabled &&
                          extensionsStore.selectedExtension?.status &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.COMPLETED &&
                          extensionsStore.selectedExtension?.status !== EXTENSION_STATUS.RETRACTED
                        )}
                      >
                        Mark Extension as Retracted
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              {!isNil(extensionsHook.selectedExtensionId) &&
                <Grid item xs={12}>
                  <Paper borderType={PaperBorderType.SOLID_BORDER} titleComponent={
                    <Grid container direction='row' justifyContent='space-between' className={classes.gridHeader}>
                      <Grid item>
                        <Typography variant='h5' component='h5'>Extension Documents & Tasks</Typography >
                      </Grid >
                    </Grid>
                  }>
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent='center'
                      >
                        <Grid item xs={12}>
                          <ExtensionTodos loanId={+extensionsHook.loanId} extensionId={extensionsHook.selectedExtensionId} />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              }
            </Grid>
          </Grid>
        </Grid>
      </VirtualScroll >
      <RequestExtensionDialog
        open={extensionsHook.isRequestExtensionDialogOpen}
        onClose={extensionsHook.handleCloseRequestExtensionDialog}
        loanId={extensionsHook.loanId}
      />
      <ConfirmDialog
        open={extensionsHook.isMarkInsuranceReviewCompleteDialogOpen}
        title={`Insurance Review Complete`}
        body={`Are you sure you want to mark insurance as complete?`}
        handleCancel={() => extensionsHook.setIsMarkInsuranceReviewCompleteDialogOpen(false)}
        handleClose={() => extensionsHook.setIsMarkInsuranceReviewCompleteDialogOpen(false)}
        handleConfirm={() => {
          extensionsHook.setIsMarkInsuranceReviewCompleteDialogOpen(false);
          extensionsStore.markInsuranceReviewComplete();
        }}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
      <ConfirmDialog
        open={extensionsHook.isRequestExtensionAgreementDialogOpen}
        title={`Request Extension Agreement`}
        body={`Are you sure you want to request extension agreement?`}
        handleCancel={() => extensionsHook.setIsRequestExtensionAgreementDialogOpen(false)}
        handleClose={() => extensionsHook.setIsRequestExtensionAgreementDialogOpen(false)}
        handleConfirm={() => {
          extensionsHook.setIsRequestExtensionAgreementDialogOpen(false);
          extensionsStore.requestExtensionAgreement()
        }}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
      <ConfirmDialog
        open={extensionsHook.isMarkAsCompleteDialogOpen}
        title={`Mark Extension as Completed`}
        body={`Are you sure you want to mark this extension as completed?`}
        handleCancel={() => extensionsHook.setIsMarkAsCompleteDialogOpen(false)}
        handleClose={() => extensionsHook.setIsMarkAsCompleteDialogOpen(false)}
        handleConfirm={() => {
          extensionsHook.setIsMarkAsCompleteDialogOpen(false);
          extensionsStore.markLoanExtensionAsComplete(extensionsHook.selectedExtensionId)
        }}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
      <ConfirmDialog
        open={extensionsHook.isMarkAsRetractedDialogOpen}
        title={`Mark Extension as Retracted`}
        body={`Are you sure you want to mark this extension as retracted?`}
        handleCancel={() => extensionsHook.setIsMarkAsRetractedDialogOpen(false)}
        handleClose={() => extensionsHook.setIsMarkAsRetractedDialogOpen(false)}
        handleConfirm={() => {
          extensionsHook.setIsMarkAsRetractedDialogOpen(false);
          extensionsStore.markLoanExtensionAsRetracted(extensionsHook.selectedExtensionId)
        }}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
    </>
  );
}
);
